import { Button, ButtonGroup, Icon, Switch } from "@blueprintjs/core";
import classNames from "classnames";
import React, { ReactNode, useState } from "react";

import styles from "./accordion.module.scss";

interface AccordionItemBase {
  id: string;
  label: string;
  bodyRender: ReactNode;
  isOpen?: boolean;
}

interface AccordionItemStandard extends AccordionItemBase {
  type: "STANDARD";
}

interface Toggle {
  checked: boolean;
  onText: string;
  offText: string;
  onChange: () => void;
}

interface AccordionItemToggle extends AccordionItemBase {
  type: "TOGGLE";
  toggle: Toggle;
}

interface ItemButtonGroup<TSelected> {
  selected: TSelected;
  options: TSelected[];
  onChange: (selected: TSelected) => void;
}

interface AccordionItemButtonGroup<TSelected> extends AccordionItemBase {
  type: "BUTTON_GROUP";
  buttonGroup: ItemButtonGroup<TSelected>;
}

export type AccordionItemProps<TButtonOption extends string = string> =
  | AccordionItemStandard
  | AccordionItemToggle
  | AccordionItemButtonGroup<TButtonOption>;

const AccordionItem = <TButton extends string>(props: AccordionItemProps<TButton>) => {
  const { type, label, bodyRender, isOpen } = props;

  const [accordionItemIsOpen, setAccordionItemIsOpen] = useState<boolean>(isOpen || false);

  const toggleAccordionItemIsOpen = () => {
    setAccordionItemIsOpen((prevState) => !prevState);
  };

  return (
    <div>
      <div id={`${props.id}-button`} className={styles.header} role="button" onClick={toggleAccordionItemIsOpen}>
        <div className={styles.label}>
          {label}
          {type === "TOGGLE" && (
            <Switch
              checked={props.toggle.checked}
              onChange={props.toggle.onChange}
              innerLabel={props.toggle.offText}
              innerLabelChecked={props.toggle.onText}
            />
          )}
          {type === "BUTTON_GROUP" && (
            <ButtonGroup className={styles.buttonGroup}>
              {props.buttonGroup.options.map((option, i) => (
                <Button
                  key={i}
                  active={props.buttonGroup.selected === option}
                  onClick={(event) => {
                    event.stopPropagation();
                    props.buttonGroup.onChange(option);
                  }}
                >
                  {option}
                </Button>
              ))}
            </ButtonGroup>
          )}
        </div>
        <Icon icon={accordionItemIsOpen ? "chevron-up" : "chevron-down"} />
      </div>
      <div className={classNames(styles.content, { [styles.collapsed || ""]: !accordionItemIsOpen })}>{bodyRender}</div>
    </div>
  );
};

interface AccordionProps<TButtonOption extends string = string> {
  items: AccordionItemProps<TButtonOption>[];
  className?: string;
}

export const Accordion = <TButtonOption extends string>({ items, className = "" }: AccordionProps<TButtonOption>) => {
  return (
    <div className={className}>
      {items.map((item) => (
        <AccordionItem {...item} key={item.id} />
      ))}
    </div>
  );
};
